import React, { useEffect } from "react";
import { View, StyleSheet, Alert, ActivityIndicator, Platform } from "react-native";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { RichEditor, RichToolbar, actions } from "react-native-pell-rich-editor";
import { FloppyDisk } from "@phosphor-icons/react/dist/csr/FloppyDisk";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { useGetCourseByIdQuery, useEditCourseMutation } from "../../../graphql/generated/graphql";
import TextEditor from "../../../components/TextEditor/TextEditor";
const courseCatalogInfoSchema = z.object({
  price: z.string().optional(),
  description: z.string().min(1, "Description is required"),
  objectives: z.string().min(1, "Objectives are required"),
  targetAudience: z.string().optional(),
});

type CourseCatalogInfoFormData = z.infer<typeof courseCatalogInfoSchema>;

interface AdminCourseCatalogueInfoContainerProps {
  courseId: string;
}

const AdminCourseCatalogueInfoContainer: React.FC<AdminCourseCatalogueInfoContainerProps> = ({ courseId }) => {
  const {
    data: courseData,
    loading,
    error,
    refetch,
  } = useGetCourseByIdQuery({
    variables: {
      id: courseId,
    },
  });

  const course = courseData?.getCourseById?.data?.[0];

  const [editCourse] = useEditCourseMutation();

  const editorRef = React.useRef<RichEditor>(null);

  useEffect(() => {
    if (course?.objectives && course?.objectives?.length > 0 && editorRef.current) {
      editorRef.current.setContentHTML(course?.objectives?.join("\n"));
    }
  }, [course?.objectives]);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
  } = useForm<CourseCatalogInfoFormData>({
    resolver: zodResolver(courseCatalogInfoSchema),
    defaultValues: {
      price: "0",
      description: "",
      objectives: "",
      targetAudience: "",
    },
  });

  useEffect(() => {
    if (course) {
      reset({
        price: course.cost?.toString() || "0",
        description: course.description || "",
        objectives: course.objectives?.join("\n") || "",
        targetAudience: course.targetAudience || "",
      });
    }
  }, [course, reset]);

  const handleSave = handleSubmit(async (formData) => {
    try {
      await editCourse({
        variables: {
          id: course.id,
          course: {
            cost: formData.price ? Number(formData.price) : null,
            description: formData.description,
            objectives: [formData.objectives],
            targetAudience: formData.targetAudience,
          },
        },
      });

      refetch();
      Alert.alert("Success", "Course updated successfully");
      reset(formData);
    } catch (error) {
      console.error("Error saving course:", error);
      Alert.alert("Error", "Failed to save course changes. Please try again.");
    }
  });

  if (loading || !course) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0000ff" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <CustomText text="Error loading course" size="l" weight="ultraBold" useTranslationText={false} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <View style={styles.form}>
          <View style={styles.formGroup}>
            <CustomText text="Course Price" style={styles.sectionTitle} useTranslationText={false} />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              disabled={!course.stripeProductId}
              name="price"
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  editable={!!course.stripeProductId}
                  onChangeText={(text) => {
                    const newPrice = Number(text);

                    if (Number.isNaN(newPrice)) {
                      Alert.alert("Invalid price", "Please enter a valid number");
                      return;
                    }

                    if (newPrice < 0) {
                      Alert.alert("Invalid price", "Please enter a price greater than 0");
                      return;
                    }

                    onChange(newPrice.toString());
                  }}
                  placeholder="0"
                  style={{}}
                  error={errors.price?.message}
                />
              )}
            />
            {errors.price && <CustomText text={errors.price.message} size="xs" weight="ultraBold" style={{}} />}
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Course Description" style={styles.sectionTitle} useTranslationText={false} />
            <CustomText
              text="This information appears on the course details page in the course catalogue."
              style={styles.subtitle}
              useTranslationText
            />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="description"
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  multiline={true}
                  onChangeText={onChange}
                  placeholder="Course Description"
                  style={{}}
                  error={errors.description?.message}
                  numberOfLines={8}
                />
              )}
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText
              text="What will the student learn in this course?"
              style={styles.sectionTitle}
              useTranslationText={false}
            />
            <CustomText
              text="This information appears on the course details page in the course catalogue and is best formatted by using bullets."
              style={styles.subtitle}
              useTranslationText={false}
            />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="objectives"
              render={({ field: { onChange, value } }) =>
                Platform.OS === "web" ? (
                  <View style={styles.textEditorContainer}>
                    <TextEditor
                      value={value}
                      onChange={(content) => {
                        onChange(content);
                        setValue("objectives", content, { shouldDirty: true });
                      }}
                      disabled={loading}
                      placeholder="Add a description of what students will learn in this course."
                      style={styles.textEditor}
                    />
                  </View>
                ) : (
                  <>
                    <RichEditor
                      ref={editorRef}
                      style={styles.richEditor}
                      initialContentHTML={value}
                      onChange={(content) => {
                        onChange(content);
                        setValue("objectives", content, { shouldDirty: true });
                      }}
                      placeholder="Add a description of what students will learn in this course."
                    />
                    <RichToolbar
                      editor={editorRef}
                      actions={[
                        actions.setBold,
                        actions.setItalic,
                        actions.setUnderline,
                        actions.heading1,
                        actions.heading2,
                        actions.insertBulletsList,
                        actions.insertOrderedList,
                        actions.insertLink,
                        actions.insertImage,
                      ]}
                      style={styles.richToolbar}
                    />
                  </>
                )
              }
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Who is this course for?" style={styles.sectionTitle} useTranslationText={false} />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="targetAudience"
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  multiline={true}
                  onChangeText={onChange}
                  placeholder="Who is this course for?"
                  style={{}}
                  error={errors.targetAudience?.message}
                  numberOfLines={8}
                />
              )}
            />
          </View>
        </View>

        <View style={styles.footer}>
          <CustomButton
            text="Save"
            styleType="primary"
            size="m"
            bold={true}
            onPress={() => handleSave()}
            leftIcon={<FloppyDisk size={24} color={!isDirty ? "#6B7280" : "#FFFFFF"} />}
            rightIcon={null}
            style={{
              backgroundColor: !isDirty ? "#E5E7EB" : "#3E68FE",
              opacity: !isDirty ? 0.5 : 1,
              width: "100%",
              justifyContent: "center",
            }}
            textStyle={{
              color: !isDirty ? "#6B7280" : "#FFFFFF",
              fontSize: 14,
            }}
            disabled={false}
            textProps={{}}
            useTranslationText={true}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  textEditorContainer: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    overflow: "hidden",
  },
  textEditor: {
    minHeight: 200,
    padding: 12,
    borderRadius: 8,
    width: "100%",
  },
  container: {
    flexDirection: "column",
    flex: 1,
    backgroundColor: "#fff",
  },
  content: {
    flexDirection: "column",
    flex: 1,
    padding: 24,
    maxWidth: 800,
    marginHorizontal: "auto",
    width: "100%",
  },
  form: {
    flex: 1,
  },
  formGroup: {
    width: "100%",
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "500",
    color: "#374151",
  },
  subtitle: {
    fontSize: 14,
    fontWeight: "400",
    color: "#475467",
    marginTop: 4,
  },
  richEditorContainer: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    overflow: "hidden",
  },
  richEditor: {
    minHeight: 200,
    maxHeight: 400,
  },
  richToolbar: {
    borderTopWidth: 1,
    borderTopColor: "#D1D5DB",
    backgroundColor: "#F9FAFB",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: 24,
    width: "100%",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
});

export default AdminCourseCatalogueInfoContainer;
